import { Globe, Heart, Shield, Award, ChevronLeft, ChevronRight } from 'lucide-react';
import { useState, useEffect } from 'react';
import PageHeader from '../components/PageHeader';
import Seo from '../components/Seo';

export default function About() {
  // Team data
  const teamMembers = [
    {
      name: "Rene Traut",
      title: "Founder and Personal Travel Manager",
      specialties: ["Luxury Travel", "Custom Itineraries"],
      image: "/rene traut.jpg",
    },
    {
      name: "Werner Traut",
      title: "Director and Personal Travel Manager",
      specialties: ["Adventure Travel", "Group Bookings"],
      image: "/werner traut.jpg",
    }
  ];

  const [index, setIndex] = useState(0);

  // Auto-carousel effect
  useEffect(() => {
    const interval = setInterval(() => {
      setIndex((prev) => (prev + 1) % teamMembers.length);
    }, 4000); // Change every 4 seconds

    return () => clearInterval(interval);
  }, [teamMembers.length]);

  const nextSlide = () => setIndex((prev) => (prev + 1) % teamMembers.length);
  const prevSlide = () => setIndex((prev) => (prev - 1 + teamMembers.length) % teamMembers.length);

  return (
    <div className="bg-white">
      <Seo
        title="About Us - Memento Travel Co."
        description="Discover our story, our passion for travel, and why we're your trusted partner for unforgettable travel adventures worldwide."
        canonicalUrl={typeof window !== 'undefined' ? window.location.href : 'https://www.mementotravel.co/about'}
        pageType="about"
      />
      <PageHeader
        title="Memento Travel Co."
        description="Discover our story, our passion for travel, and why we're your trusted partner for unforgettable South African adventures."
      />
      <div className="py-24 sm:py-32">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        {/* Why Choose Memento travel */}
        <div className="mx-auto max-w-3xl text-center mb-20">
            <h2 className="text-4xl md:text-5xl font-serif text-gray-900 mb-6">
              <span style={{color: '#006D77'}}>A Journey Born from Passion</span>
          </h2>
          <p className="text-lg text-gray-700">
          Memento Travel Co. isn't just a business, it's the culmination of a lifetime of global adventures, industry expertise, and an unwavering desire to help others explore the wonders of our world. Founded by Rene Traut, our story began on the decks of luxury cruise ships, navigating through ocean breezes and exotic ports that would forever change the course of one traveler's life.          </p>
        </div>

        {/* Trusted Travel Partner Section */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center mb-20">
          <div className="rounded-2xl overflow-hidden shadow-lg h-80 bg-gray-100 flex items-center justify-center">
            {/* Placeholder image, replace src with real image if available */}
            <img src="/shutterstock_2176161815-scaled (1).jpg" alt="Travel destination" className="object-cover w-full h-full" />
          </div>
          <div>
            <h3 className="text-3xl font-serif text-gray-900 mb-4">What Sets Us Apart</h3>
            <p className="text-lg text-gray-700 mb-6">
            Global Insight: Having circumnavigated the globe twice, our founder's firsthand experiences influence every itinerary we craft. We don't just book trips we share the wisdom of wanderlust.            </p>
            <p className="text-lg text-gray-700 mb-6">
            Passion for Personalized Service: From travel expert to senior manager, we've mastered the art of tailoring journeys to meet individual desires. Every traveler is unique, and so should be their adventure.            </p>
            <p className="text-lg text-gray-700 mb-6">
            Heartfelt Assistance: At Memento Travel Co., we're not just planners, we're companions on your journey, ensuring every step is filled with joy, discovery, and wonder.            </p>
          </div>
        </div>

        {/* Your Adventure Awaits Section - Centered */}
        <div className="mx-auto max-w-4xl text-center mb-20">
          <h3 className="text-3xl font-serif text-gray-900 mb-6">Your Adventure Awaits</h3>
          <p className="text-lg text-gray-700 mb-6">
            Whether you dream of wandering through historic cities, lounging on pristine beaches, or embracing the thrill of new adventures, Memento Travel Co. is here to turn your dreams into reality. Our luxury seaside accommodations and carefully curated experiences reflect the same attention to detail and passion for excellence that has guided our founder's remarkable journey.
          </p>
          <p className="text-lg text-gray-700">
            Let's embark on many more adventures together because every journey should be a memento worth treasuring.
          </p>
        </div>


        {/* Meet Our Team Section */}
        <div className="mt-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Left Title Section */}
            <div className="lg:col-span-1">
              <h2 className="text-4xl md:text-5xl lg:text-6xl font-serif leading-tight text-gray-900">
                MEET <span className="italic" style={{color: '#006D77'}}>OUR</span> TEAM
              </h2>
              <p className="mt-6 text-gray-600 max-w-sm text-lg">
                When you need expert travel planning and personalized service, you can trust our experienced team at Memento Travel Co.
              </p>
              </div>

            {/* Carousel Section */}
            <div className="lg:col-span-1 flex flex-col items-center justify-center">
            <div className="relative w-[320px] h-[400px]">
              {teamMembers.map((member, i) => {
                const offset = (i - index + teamMembers.length) % teamMembers.length;
                const isActive = offset === 0;
                const isVisible = offset <= 2;

                return (
                  <div
                    key={member.name}
                    className="absolute w-full h-full rounded-2xl overflow-hidden shadow-lg transition-all duration-500 ease-in-out"
                    style={{ 
                      zIndex: teamMembers.length - offset,
                      transform: `translateX(${offset * 50}px) scale(${isActive ? 1 : 0.9})`,
                      opacity: isVisible ? 1 : 0
                    }}
                  >
                    <img
                      src={member.image}
                      alt={member.name}
                      className="w-full h-full object-cover"
                    />
              </div>
                );
              })}
            </div>

            {/* Info Section */}
            <div className="text-center mt-6">
              <h3 className="text-2xl font-serif text-gray-900">
                {teamMembers[index].name}
              </h3>
              <p className="mt-1" style={{color: '#006D77'}}>{teamMembers[index].title}</p>
        </div>

            {/* Navigation */}
            <div className="flex items-center space-x-4 mt-6">
              <button
                onClick={prevSlide}
                className="p-2 rounded-full border border-gray-400 hover:bg-gray-100 transition-colors"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <button
                onClick={nextSlide}
                className="p-2 rounded-full border border-gray-400 hover:bg-gray-100 transition-colors"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
          </div>

            {/* Pagination */}
            <p className="mt-4 text-gray-500">
              {index + 1} / {teamMembers.length}
            </p>
          </div>
          </div>
          </div>
        </div>

        {/* Partners Section */}
        <div className="mt-20">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-serif text-gray-900 mb-4">
              Our <span style={{color: '#006D77'}}>Partners</span>
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              We're proud to work with trusted industry partners who share our commitment to excellence in travel.
            </p>
          </div>
          
          <div className="flex flex-col sm:flex-row items-center justify-center gap-12">
            {/* IATA Logo */}
            <div className="flex items-center justify-center p-6 bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300">
              <img 
                src="/iata-removebg-preview.png" 
                alt="IATA - International Air Transport Association" 
                className="h-16 w-auto object-contain"
              />
            </div>
            
            {/* Club Travel Logo - Clickable */}
            <div className="flex items-center justify-center p-6 bg-white rounded-lg shadow-md hover:shadow-lg transition-all duration-300 hover:scale-105 cursor-pointer">
              <a 
                href="/Club Travel Letter of Confirmation - Memento Travel.pdf" 
                target="_blank" 
                rel="noopener noreferrer"
                className="block"
              >
                <img 
                  src="/club_logo-removebg-preview.png" 
                  alt="Club Travel" 
                  className="h-16 w-auto object-contain"
                />
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
} 